import React, { useState } from 'react';
import { apiService, Appointment } from '../../services/apiService';

interface AppointmentModalProps {
  appointment: Appointment;
  onClose: () => void;
  onUpdate: () => void;
  onEdit: () => void;
}

const AppointmentModal: React.FC<AppointmentModalProps> = ({ appointment, onClose, onUpdate, onEdit }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleStatusChange = async (newStatus: Appointment['status']) => {
    try {
      setLoading(true);
      setError(null);
      
      if (newStatus === 'cancelled') {
        await apiService.cancelAppointment(appointment.id);
      } else {
        await apiService.updateAppointment(appointment.id, { status: newStatus });
      }
      
      onUpdate();
    } catch (err) {
      console.error('Error updating appointment:', err);
      setError('Failed to update appointment');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this appointment?')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      await apiService.deleteAppointment(appointment.id);
      onUpdate();
    } catch (err) {
      console.error('Error deleting appointment:', err);
      setError('Failed to delete appointment');
    } finally {
      setLoading(false);
    }
  };

  const openGoogleCalendar = () => {
    if (appointment.google_calendar_event_id) {
      const eventUrl = `https://calendar.google.com/calendar/event?eid=${appointment.google_calendar_event_id}`;
      window.open(eventUrl, '_blank');
    }
  };

  const formatPhoneNumber = (phone: string) => {
    // Basic phone number formatting for display
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
    }
    return phone;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString: string) => {
    return new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-blue-100 text-blue-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'no_show':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'Confirmed';
      case 'scheduled':
        return 'Scheduled';
      case 'completed':
        return 'Completed';
      case 'cancelled':
        return 'Cancelled';
      case 'no_show':
        return 'No Show';
      default:
        return status;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-bold text-gray-900">Appointment Details</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* Patient Information */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Patient Information</h3>
              <div className="space-y-2">
                <p><span className="font-medium">Name:</span> {appointment.patient_name}</p>
                <p><span className="font-medium">Email:</span> {appointment.patient_email}</p>
                <p><span className="font-medium">Phone:</span> {formatPhoneNumber(appointment.patient_phone)}</p>
              </div>
            </div>

            {/* Appointment Details */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">Appointment Details</h3>
              <div className="space-y-2">
                <p><span className="font-medium">Date:</span> {formatDate(appointment.appointment_date)}</p>
                <p><span className="font-medium">Time:</span> {formatTime(appointment.start_time)} - {formatTime(appointment.end_time)}</p>
                <p><span className="font-medium">Provider:</span> {appointment.provider_name}</p>
                <p><span className="font-medium">Procedure:</span> {appointment.procedure_name}</p>
                <p><span className="font-medium">Operatory:</span> {appointment.operatory_name}</p>
                <p><span className="font-medium">Status:</span> 
                  <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(appointment.status)}`}>
                    {getStatusLabel(appointment.status)}
                  </span>
                </p>
              </div>
            </div>

            {/* Notes */}
            {appointment.notes && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
                <p className="text-gray-700">{appointment.notes}</p>
              </div>
            )}

            {/* Google Calendar Link */}
            {appointment.google_calendar_event_id && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-900 mb-2">Google Calendar</h3>
                <button
                  onClick={openGoogleCalendar}
                  className="text-blue-600 hover:text-blue-800 underline"
                >
                  View in Google Calendar
                </button>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={onEdit}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Edit
            </button>
            
            {appointment.status === 'scheduled' && (
              <button
                onClick={() => handleStatusChange('confirmed')}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
              >
                Confirm
              </button>
            )}
            
            {appointment.status === 'confirmed' && (
              <button
                onClick={() => handleStatusChange('completed')}
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                Complete
              </button>
            )}
            
            {appointment.status !== 'cancelled' && appointment.status !== 'completed' && (
              <button
                onClick={() => handleStatusChange('cancelled')}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
              >
                Cancel
              </button>
            )}
            
            {appointment.status === 'confirmed' && (
              <button
                onClick={() => handleStatusChange('no_show')}
                disabled={loading}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
              >
                No Show
              </button>
            )}
            
            <button
              onClick={handleDelete}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppointmentModal; 